import ReactECharts from 'echarts-for-react';
import studioData from '../data/studioMethodology.json';

/**
 * ProductPortfolioChart — the real product roster (Fig 2), grouped by status
 * (active / legacy / offline). Data is the actual product list, descriptions,
 * and brand colors from Playano_Ed_HP_4/public/index.html — no invented score
 * or axis, just what exists today (structural fact, not a measurement).
 */

type Product = { name: string; status: string; color: string; description: string };

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  legacy: 'Legacy (unsupported)',
  offline: 'Offline',
};

export const ProductPortfolioChart = ({ height = 420 }: { height?: number }) => {
  const items = (studioData as unknown as { products: { items: Product[] } }).products.items;

  const byStatus: Record<string, Product[]> = {};
  for (const p of items) {
    (byStatus[p.status] ??= []).push(p);
  }

  const treeData = Object.entries(byStatus).map(([status, products]) => ({
    name: STATUS_LABEL[status] ?? status,
    itemStyle: { color: '#262626', borderColor: '#333' },
    children: products.map((p) => ({
      name: p.name,
      value: 1,
      itemStyle: { color: p.color },
    })),
  }));

  const option = {
    backgroundColor: '#171717',
    tooltip: {
      formatter: (p: { name: string; treePathInfo?: { name: string }[] }) => {
        const match = items.find((i) => i.name === p.name);
        return match ? `<b>${match.name}</b><br/>${match.description}` : p.name;
      },
      backgroundColor: '#0a0a0a',
      borderColor: '#333',
      textStyle: { color: '#f5f5f5', fontSize: 13 },
    },
    series: [
      {
        type: 'treemap',
        roam: false,
        nodeClick: false,
        breadcrumb: { show: false },
        label: {
          show: true,
          color: '#111',
          fontWeight: 700,
          fontSize: 13,
        },
        upperLabel: {
          show: true,
          height: 28,
          color: '#e5e5e5',
          fontWeight: 700,
        },
        itemStyle: { borderColor: '#171717', borderWidth: 2, gapWidth: 3 },
        levels: [
          {},
          {
            itemStyle: { borderColor: '#171717', borderWidth: 3, gapWidth: 4 },
            upperLabel: { show: true },
          },
        ],
        data: treeData,
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-[#333] bg-[#171717] p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-gray-400 text-center px-4">
        Figure 2. The Playano product roster, grouped by status. Each tile is one product; hover for
        its one-line description.
      </p>
    </div>
  );
};
